import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
  buttonFunctions,
} from '../../shared/Button';
import Table from '../../shared/table/Table';
import { selectorTypes } from '../../shared/table/TableRow';
import SvgXIcon from '../../svg/XIcon';
import {
  searchText,
  submitText,
  cancelText,
  idText,
  descriptionText,
  selectedDevicesText,
  notesText,
} from '../../shared/SharedInternationalizations';
import InlineHelpText from '../../shared/InlineHelpText';
import HelpIcon from '../../shared/HelpIcon';
import {
  loadAlarmDeviceTypes,
  loadUnassignedPoiDevicesDetails,
} from '../../../redux/actions/alarmDeviceActions';
import Pager from '../../shared/Pager';
import { loadDeviceAssociations } from '../../../redux/actions/deviceActions';
import LoadingSpinner from '../../shared/LoadingSpinner';

/**
 * Form component that allows bulk associating of users to a unit
 * @param {Function} props.onSubmit - Function to be performed when the form is submitted
 * @param {array} props.userArray - Array of all of the users
 * @param {string} props.unit - The id for the unit that the users will be associated with
 * @param {array} props.users - Any users that have been preselected elsewhere and passed to prepopulate the selected users table
 */
export function UnconnectedAssociatePOIForm(props) {
  const { onSubmit, associateId, devices, previous, type, apiSpinner } = props;

  // Devices come in as an object, make them into an array
  const devicesArr = Object.values(devices);

  // filter out any devices that aren't hardware type 12 (POIs)
  const initialDevicesArr = devicesArr.filter((d) => {
    if (d.hardware_type === 12) {
      // Filter out any assigned devices
      if (d[type].includes(associateId)) {
        return true;
      }
    } 
      return false;
   
  });

  // map over the new array of filtered devices to make an array of just the ids for those devices
  const initialDevices = initialDevicesArr.map((d) => d.alarm_device_id);

  // load unassigned POI details and device associations once when the component loads for the first time
  useEffect(() => {
    props.dispatch(loadUnassignedPoiDevicesDetails());
    props.dispatch(loadDeviceAssociations());
  }, []); // eslint-disable-line

  // local state for the search filter input on the form
  const [devicesFilter, setDevicesFilter] = useState('');

  // show/hide the helptext
  const [
    showSelectedDevicesHelpText,
    setShowSelectedDevicesHelpText,
  ] = useState(false);

  const selectedDevicesHelpTextRef = useRef();
  function toggleSelectedDevicesHelpText(value) {
    setShowSelectedDevicesHelpText(value);
  }

  // translated string for the help text
  const selectedDevicesHelpText = (
    <FormattedMessage
      id="associatePOIDevicesForm.selectedDevicesHelpText"
      defaultMessage="The devices that have been selected. To remove a device from this list, click the 'x' icon. To add a device, click the '+' icon."
      description="Help text."
    />
  );

  const [pagerParams, setPagerParams] = useState({
    totalLength: 0,
    startValue: 0,
    endValue: 49,
    incrementValue: 50,
  });

  function setPager(startValue) {
    const pagerValues = { ...pagerParams };
    let start = startValue;
    if (start <= 0) {
      pagerValues.startValue = 0;
      pagerValues.endValue = pagerParams.incrementValue - 1;
    } else {
      pagerValues.startValue = start;
      pagerValues.endValue = startValue + pagerParams.incrementValue - 1;
    }
    setPagerParams(pagerValues);
  }

  useEffect(() => {
    props.dispatch(loadAlarmDeviceTypes());
  }, []); // eslint-disable-line

  /**
   * Function to set the search value for active filtering for users
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setUsersFilter - Updates local state with the value typed into the search bar
   */
  const devicesSearchFilter = (event) => {
    const value = event.target.value;
    const pagerValues = { ...pagerParams };
    pagerValues.startValue = 0;

    setPagerParams(pagerValues);
    setDevicesFilter(value);
  };

  /**
   * Function to generate the users table
   * @returns {object} - The table of users
   */
  const devicesTable = (values) => {
    // map over the users and put into the format that is required for the table component
    const initialFilterList = devicesArr.filter((d) => {
      if (values.devices.includes(d.alarm_device_id)) {
        return false;
      } else if (d[type].includes(associateId)) {
        return true;
      } else if (d.hardware_type !== 12) {
        return false;
      } else if (d.user.length > 0 || d.unit.length > 0) {
        return false;
      } else {
        return true;
      }
    });

    const devicesArray = initialFilterList.map((d) => {
      return {
        selector: {
          sortBy: d.alarm_device_id,
          display: d.alarm_device_id,
        },
        id: {
          sortBy: d.alarm_device_id,
          display: d.alarm_device_id,
        },
        description: {
          sortBy: d.alarm_description.toUpperCase(),
          display: d.alarm_description,
        },
        type: {
          sortBy: d.alarm_device_type,
          display: d.notes,
        },
      };
    });

    let searchedDevices = [];
    // Take the array and filter based on the text entered in the search bar.
    if (devicesFilter !== '') {
      searchedDevices = devicesArray.filter((item) => {
        let filterResult = false;
        // Make the device name uppercase for case insensitivity
        const description = item.description.display.toUpperCase();
        let type = '';
        if (item.type.display) {
          type = item.type.display.toUpperCase();
        }
        const id = item.id.display;
        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        if (
          description.includes(devicesFilter.toUpperCase()) ||
          devicesFilter.includes(id) ||
          type.includes(devicesFilter.toUpperCase())
        ) {
          filterResult = true;
        }
        return filterResult;
      });
    } else {
      searchedDevices = devicesArray;
    }

    if (searchedDevices.length !== pagerParams.totalLength) {
      const pagerValues = { ...pagerParams };
      pagerValues.totalLength = searchedDevices.length;
      setPagerParams(pagerValues);
    }

    let pagedDevices = [];
    searchedDevices.map((d, index) => {
      if (index >= pagerParams.startValue && index <= pagerParams.endValue) {
        pagedDevices.push(d);
      }
      return '';
    });

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },
      {
        isSortable: true,
        sortBy: 'id',
        display: idText,
      },
      {
        isSortable: true,
        sortBy: 'description',
        display: descriptionText,
      },
      {
        isSortable: false,
        sortBy: 'type',
        display: notesText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="devices"
        selectorParamValue="devices"
        sortParam="id"
        tableData={pagedDevices}
        selectorType={selectorTypes.checkbox}
        tableStyles="table__constrained table-layout__3-column--selector"
        data-test="associate-alarm-devices-table"
      />
    );

    if (pagedDevices.length > 0 || apiSpinner.alarmDevicesReturned) {
      return table;
    } else {
      return <LoadingSpinner />;
    }
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const selectedDevicesTable = (values) => {
    let table;
    let deviceId = values.devices;

    let deviceRows;
    if (deviceId.length > 0) {
      if (deviceId) {
        const filteredDevices = [];
        deviceId.map((id) => {
          let thisId = id;
          if (typeof id === 'string') {
            thisId = parseInt(thisId);
          }
          if (devices[thisId]) {
            filteredDevices.push(devices[thisId]);
          }
          return '';
        });
        deviceRows = filteredDevices.map((d) => {
          const removeButton = (
            <label>
              <Field
                className="visually-hidden"
                name="devices"
                component="input"
                type="checkbox"
                value={d.alarm_device_id}
              />
              <SvgXIcon />
            </label>
          );

          return {
            button: {
              sortBy: '',
              display: removeButton,
            },
            id: {
              sortBy: d.alarm_device_id,
              display: d.alarm_device_id,
            },
            description: {
              sortBy: d.alarm_description.toUpperCase(),
              display: d.alarm_description,
            },
            type: {
              sortBy: d.alarm_device_type,
              display: d.notes,
            },
          };
        });
      }

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: true,
          sortBy: 'id',
          display: idText,
        },
        {
          isSortable: true,
          sortBy: 'description',
          display: descriptionText,
        },
        {
          isSortable: false,
          sortBy: 'type',
          display: notesText,
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="id"
          tableData={deviceRows}
          tableStyles="table__constrained table-layout__3-column--selector"
          data-test="associate-alarm-selected-devices-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  return (
    <React.Fragment>
      <div className="associate-users-units__message-container" />
      <Form
        data-test="associate-alarm-devices-form"
        onSubmit={onSubmit}
        keepDirtyOnReinitialize={true}
        initialValues={{
          associateId: associateId,
          devices: initialDevices || [],
          initialDevices: initialDevices || [],
          type: 'alarm',
        }}
        render={({
          handleSubmit,
          pristine,
          invalid,
          reset,
          submitting,
          values,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="associate-users-units__form-container">
            <Field
              className="visually-hidden"
              name="associateId"
              component="input"
              type="text"
            />
            <Field
              className="visually-hidden"
              name="initialDevices"
              component="input"
              type="select"
            />
            <Field
              className="visually-hidden"
              name="type"
              component="input"
              type="text"
            />
            <div className="form__section-container form__section-container--grid">
              <div className="form__item-container">
                <div className="align-center">
                  <label
                    className="text-medium text-bold form-item__field-message--required"
                    htmlFor="usersId">
                    {selectedDevicesText}
                  </label>
                  <HelpIcon
                    onClick={() =>
                      selectedDevicesHelpTextRef.current.toggleHelpText()
                    }
                    toggled={showSelectedDevicesHelpText}
                    styles="margin-left-5"
                  />
                </div>
              </div>
            </div>
            <div className="width-100 margin-bottom-10">
              <InlineHelpText
                ref={selectedDevicesHelpTextRef}
                toggleFunction={toggleSelectedDevicesHelpText}
                message={selectedDevicesHelpText}
              />
            </div>
            <div className="form__section-container form__section-container--grid margin-bottom-10">
              {selectedDevicesTable(values)}

              <div className="table__header padding-10">
                <label className="text-medium text-bold" htmlFor="usersFilter">
                  {searchText}:
                </label>

                <input
                  type="text"
                  id="usersFilter"
                  name="usersFilter"
                  onChange={devicesSearchFilter}
                  className="message-table__filter-search-box margin-left-10"
                  value={devicesFilter}
                />
              </div>

              {devicesTable(values)}
              <Pager
                totalLength={pagerParams.totalLength}
                startValue={pagerParams.startValue}
                incrementValue={pagerParams.incrementValue}
                onClick={setPager}
              />
            </div>
            <div className="form__section-container form__section-container--spread associate-users-units__form-buttons-container">
              <div className="form__items-group-container" />
              <div className="form__items-group-container">
                <Link
                  className="link link-text"
                  to={previous}
                  data-test="associate-devices-form-cancel-link">
                  {cancelText}
                </Link>
                <Button
                  buttonFunction={buttonFunctions.submit}
                  text={submitText}
                  buttonType={buttonTypes.success}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin margin-left-15"
                />
              </div>
            </div>
          </form>
        )}
      />
    </React.Fragment>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    devices: state.devices.alarmDeviceDetails,
    apiSpinner: state.ui.apiSpinner,
  };
};
export default connect(mapStateToProps)(UnconnectedAssociatePOIForm);
